import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useCreateApiKeyMutation, useDeleteApiKeyMutation, useGetApiKeysListQuery, useUpdateApiKeyMutation } from 'services/apiKeys';

import { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel } from 'types/account';
import { CreateApiKeyResponse } from 'types/apiKeys';
import { APIError, ValidationErrorMeta } from 'types/error';
import { NotificationSeverity } from 'types/notifications';
import { UserAccessLevel } from 'types/user';

import { getValidationDetails } from 'utils/error';

import useGetUuid from '../utils';
import { APIKeysSection } from './apiKeys';
import { LABELS } from './apiKeys.keys';
import { ApiKeyTableRow } from './types';
import { transformApiKeysDataToTableFormat } from './utils';

const DEFAULT_ERROR_FIELD = 'name';

const getErrorDetails = (errorBase: FetchBaseQueryError | SerializedError | undefined, defaultErrorField = DEFAULT_ERROR_FIELD) => {
  if (!errorBase) {
    return undefined;
  }

  const errorMeta = errorBase ? ((errorBase as FetchBaseQueryError).data as { meta: ValidationErrorMeta }).meta : undefined;

  if (errorMeta && Object.values(errorMeta).length) {
    return getValidationDetails(errorMeta);
  }

  return { [defaultErrorField]: ((errorBase as FetchBaseQueryError).data as APIError).errorMessage };
};

export interface ApiKeyFormPayload {
  name: string;
  whitelistedIps: string[];
}

export const APIKeysSectionContainer = () => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const accountAccessLevel = useSelector(selectAccountAccess);
  const userLevel = useSelector(selectUserLevel);
  const uuid = useGetUuid();

  const isCyclopsStaff = Boolean(userLevel) && userLevel !== UserAccessLevel.Partner;
  const isCyclopsAdmin = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const isPartnerAdmin =
    Boolean(accountAccessLevel) && [AccountAccessLevel.Owner, AccountAccessLevel.Administrator].includes(accountAccessLevel!);
  const canManageApiKeys = isCyclopsStaff || isPartnerAdmin;
  const canManageApiKeyActions = isCyclopsAdmin || isPartnerAdmin;

  const { data, isLoading, isUninitialized } = useGetApiKeysListQuery(uuid!, {
    skip: !uuid || !canManageApiKeys,
  });

  const [createApiKey, { isLoading: isCreating, error: createErrorBase, reset: resetCreate }] = useCreateApiKeyMutation();
  const [updateApiKey, { isLoading: isUpdating, error: updateErrorBase, reset: resetUpdate }] = useUpdateApiKeyMutation();
  const [deleteApiKey, { isLoading: isDeleting }] = useDeleteApiKeyMutation();

  const isPending = isUninitialized || isLoading;
  const rows: ApiKeyTableRow[] = transformApiKeysDataToTableFormat(data?.keys);

  const handleCreate = async (payload: ApiKeyFormPayload): Promise<{ success: boolean; createdKey?: CreateApiKeyResponse }> => {
    const response = await createApiKey({ accountUuid: uuid!, ...payload });

    if ('data' in response) {
      return { success: true, createdKey: response.data };
    }

    return { success: false };
  };

  const handleUpdate = async (apiKeyUuid: string, payload: ApiKeyFormPayload): Promise<boolean> => {
    const response = await updateApiKey({ accountUuid: uuid!, apiKeyUuid, ...payload });

    return 'data' in response;
  };

  const handleDelete = async (apiKeyUuid: string): Promise<boolean> => {
    const response = await deleteApiKey({ accountUuid: uuid!, apiKeyUuid });

    if ('data' in response) {
      pushToast({ severity: NotificationSeverity.success, message: t(LABELS.DELETE_MODAL.SUCCESS) });
      return true;
    }

    const { error } = response as { error: SerializedError | FetchBaseQueryError };
    if ('data' in error) {
      const { errorMessage } = error.data as APIError;
      pushToast({ severity: NotificationSeverity.error, message: errorMessage });
    }

    return false;
  };

  return (
    <APIKeysSection
      canManage={canManageApiKeys}
      canManageActions={canManageApiKeyActions}
      kybApproved={data?.kybApproved ?? true}
      kybMessage={data?.message}
      rows={rows}
      isLoading={isPending}
      createApiKey={{
        onCreate: handleCreate,
        isSubmitting: isCreating,
        error: getErrorDetails(createErrorBase),
        reset: resetCreate,
      }}
      updateApiKey={{
        onUpdate: handleUpdate,
        isSubmitting: isUpdating,
        error: getErrorDetails(updateErrorBase),
        reset: resetUpdate,
      }}
      deleteApiKey={{
        onDelete: handleDelete,
        isSubmitting: isDeleting,
      }}
    />
  );
};
